import { ReactNode } from "react";
import { Info, AlertTriangle, Lightbulb, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type InfoCardVariant = "info" | "warning" | "tip" | "success";

interface InfoCardProps {
  variant?: InfoCardVariant;
  title?: string;
  children: ReactNode;
  className?: string;
}

const variantStyles: Record<
  InfoCardVariant,
  { bg: string; border: string; icon: typeof Info; iconColor: string }
> = {
  info: {
    bg: "bg-secondary/10",
    border: "border-secondary/30",
    icon: Info,
    iconColor: "text-secondary",
  },
  warning: {
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    icon: AlertTriangle,
    iconColor: "text-amber-500",
  },
  tip: {
    bg: "bg-primary/10",
    border: "border-primary/30",
    icon: Lightbulb,
    iconColor: "text-primary",
  },
  success: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    icon: CheckCircle,
    iconColor: "text-emerald-500",
  },
};

const InfoCard = ({ variant = "info", title, children, className }: InfoCardProps) => {
  const styles = variantStyles[variant];
  const Icon = styles.icon;

  return (
    <div className={cn("rounded-lg border p-4", styles.bg, styles.border, className)}>
      <div className="flex gap-3">
        <Icon className={cn("mt-0.5 h-5 w-5 flex-shrink-0", styles.iconColor)} />
        <div className="flex-1">
          {title && <h4 className="mb-1 font-medium text-foreground">{title}</h4>}
          <div className="text-sm text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default InfoCard;
